import React, { useState } from 'react'
import { WorkflowDetails } from '../../../components/CustomWorkflows/WorkflowDetails'
import { Form, OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useAppDispatch } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import {
  type CustomWorkflowTriggerType,
  CustomWorkflowSummary,
  useDisableCustomWorkflowMutation,
  useEnableCustomWorkflowMutation,
} from '../../../store/customWorkflow/customWorkflows'
import { WorkflowParametersSection } from '../../../components/CustomWorkflows/parameters/WorkflowParametersSection'
import { EnterpriseManagedBanner } from '../../Setup/Settings/EnterpriseManagedBanner'
import { useWorkflowParametersModal } from './useWorkflowParametersModal'
import { workflowEnablement } from './workflowEnablement'

interface ToggleCopy {
  title: string
  description: string
  banner: string | null
}

type ToggleMode = 'required' | 'organization' | 'user'

const runMoment = (triggerType: CustomWorkflowTriggerType): string =>
  triggerType === 'hook' ? 'before the step it customizes' : 'whenever its trigger event fires'

const toggleCopy = (mode: ToggleMode, triggerType: CustomWorkflowTriggerType): ToggleCopy => {
  const moment = runMoment(triggerType)
  switch (mode) {
    case 'required':
      return {
        title: 'Required by your enterprise',
        description: `It runs automatically for everyone in your organization ${moment}.`,
        banner: "Your enterprise requires this workflow, so it can't be turned off here.",
      }
    case 'organization':
      return {
        title: 'Enable for your organization',
        description: `When enabled, it runs automatically for everyone in your organization ${moment}.`,
        banner: 'This workflow applies to everyone in your organization. Only your account admin can turn it on or off.',
      }
    case 'user':
      return {
        title: 'Enable this workflow',
        description: `When enabled, it runs automatically ${moment}.`,
        banner: null,
      }
  }
}

interface LockedSwitchProps {
  checked: boolean
  reason: string
}

const LockedSwitch: React.FC<LockedSwitchProps> = ({ checked, reason }) => (
  <OverlayTrigger trigger={['hover', 'focus']} placement='top' overlay={<BootstrapTooltip className='zocks-text-gray-500'>{reason}</BootstrapTooltip>}>
    <span className='d-inline-block' tabIndex={0}>
      <Form.Switch role='switch' aria-label='Enable this workflow' checked={checked} disabled={true} readOnly />
    </span>
  </OverlayTrigger>
)

interface EnableWorkflowModalProps {
  accountId: string
  workflow: CustomWorkflowSummary
  enablementId: string | null
  show: boolean
  onHide: () => void
}

export const EnableWorkflowModal: React.FC<EnableWorkflowModalProps> = ({ accountId, workflow, enablementId, show, onHide }) => {
  const dispatch = useAppDispatch()
  const [enableCustomWorkflow, { isLoading: enabling }] = useEnableCustomWorkflowMutation()
  const [disableCustomWorkflow, { isLoading: disabling }] = useDisableCustomWorkflowMutation()
  const [override, setOverride] = useState<boolean | null>(null)
  const { modalProps, sectionProps } = useWorkflowParametersModal(workflow.id, onHide)

  const enablement = workflowEnablement(workflow, enablementId !== null)
  const required = enablement === 'required'
  const accountLevel = workflow.level === 'account'
  const serverEnabled = enablement !== 'disabled'
  if (override !== null && override === serverEnabled) {
    setOverride(null)
  }
  const checked = override ?? serverEnabled
  const busy = enabling || disabling
  const copy = toggleCopy(required ? 'required' : accountLevel ? 'organization' : 'user', workflow.triggerType)

  const handleToggle = async (next: boolean) => {
    setOverride(next)
    try {
      if (next) {
        await enableCustomWorkflow({ accountId, customWorkflowId: workflow.id }).unwrap()
      } else if (enablementId) {
        await disableCustomWorkflow({ accountId, enabledCustomWorkflowId: enablementId }).unwrap()
      }
    } catch {
      setOverride(!next)
      dispatch(addToast({ variant: 'error', title: 'Could not update workflow', messages: ['Please try again.'] }))
    }
  }

  return (
    <Modal
      show={show}
      {...modalProps}
      bodyClassName='pb-4'
      header={<h5 className='mb-0'>{workflow.name || workflow.id}</h5>}
      body={
        <div className='d-flex flex-column zocks-gap-2'>
          <WorkflowDetails workflow={workflow} className='paragraph-medium zocks-text-gray-700' />
          {copy.banner ? <EnterpriseManagedBanner className='zocks-p-1.25 mx-0' message={copy.banner} /> : null}
          <div className='d-flex justify-content-between align-items-center border-top pt-3'>
            <div>
              <div className='fw-semibold d-flex align-items-center gap-2'>
                {required ? <i className='bi bi-lock zocks-text-gray-700' aria-hidden='true' /> : null}
                {copy.title}
              </div>
              <div className='paragraph-small zocks-text-gray-600'>{copy.description}</div>
            </div>
            {required ? (
              <LockedSwitch checked={true} reason="Your enterprise requires this workflow, so it can't be turned off." />
            ) : (
              <ScopedBlock
                scope={
                  accountLevel ? { scope: 'custom_workflow:admin', superScope: [] } : { scope: 'custom_workflow:create', superScope: ['custom_workflow:admin'] }
                }
                missingScopeAction={accountLevel ? MissingScopeAction.REPLACE : MissingScopeAction.DISABLE}
                replaceElement={<LockedSwitch checked={checked} reason='Your account admin manages this workflow.' />}
              >
                <Form.Switch
                  role='switch'
                  aria-label='Enable this workflow'
                  checked={checked}
                  disabled={busy}
                  onChange={(e) => handleToggle(e.target.checked)}
                />
              </ScopedBlock>
            )}
          </div>
          <WorkflowParametersSection accountId={accountId} workflowId={workflow.id} {...sectionProps} />
        </div>
      }
    />
  )
}
